import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { HttpErrorsService } from "@sharedV11/services/alerts/errors/http-errors.service";
import { SnackbarInfoService } from "@sharedV11/services/alerts/snackbar-info/snackbar-info.service";
import { ConectarApiService } from "@sharedV11/services/api/connection/conectar-api.service";
import { PanelService } from "@sharedV11/services/panel/panel.service";
import { Translator } from "@sharedV11/services/translator/translator.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-edit-lugares",
  templateUrl: "./edit-lugares.component.html",
  styleUrls: ["./edit-lugares.component.scss"],
})
export class EditLugaresComponent implements OnInit, OnDestroy {
  @Input() pkId: string;
  @Input() mode: string;
  @Input() modificar: boolean;

  loading = true;
  translation: any;
  editRegistroForm: FormGroup;
  private destroy$ = new Subject<void>();
  private originalValues: { pkId: number; Nombre: string; Descripcion: string };
  private readonly NOMBRE_MAX_LENGTH = 20;
  private readonly DESCRIPCION_MAX_LENGTH = 50;

  constructor(
    private fb: FormBuilder,
    private translator: Translator,
    private httpErrorService: HttpErrorsService,
    private snackvar_service: SnackbarInfoService,
    private panelService: PanelService,
    private servicioAPI: ConectarApiService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.translation = this.translator.GetTranslations();
    this.initializeForm();
    this.registerDirtyWatcher();
    this.manageForm(this.pkId, this.mode);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeForm() {
    this.editRegistroForm = this.fb.group({
      pkId: [0],
      Nombre: ["", [Validators.required, Validators.maxLength(this.NOMBRE_MAX_LENGTH)]],
      Descripcion: ["", [Validators.maxLength(this.DESCRIPCION_MAX_LENGTH)]],
    });
  }

  private registerDirtyWatcher() {
    if (!this.editRegistroForm) {
      return;
    }
    // Usar uma flag para ignorar mudanças durante o carregamento inicial
    let isInitialLoad = true;
    
    this.editRegistroForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Ignorar mudanças durante o carregamento inicial
        if (!isInitialLoad) {
          this.panelService.setDiscardChanges(false);
        }
      });
    
    // Marcar que o carregamento inicial terminou após um pequeno delay
    setTimeout(() => {
      isInitialLoad = false;
    }, 100);
  }

  async manageForm(pkId: string, mode: string) {
    if (pkId && mode !== "I") {
      const paginator = {
        page: 1,
        pagelements: 1,
        orderby: "pkId",
        ordertype: "ASC",
      };
      const filters = [{ name: "pkId", value: pkId }];

      this.servicioAPI
        .getLugaresAsync(paginator, filters)
        .then((data) => {
          if (data?.DatosResult?.customanLugares?.length) {
            const lugar = data.DatosResult.customanLugares[0];
            const isDuplicate = mode === "D";
            const nombreDuplicado = isDuplicate
              ? this.buildDuplicateName(lugar.Nombre ?? "")
              : lugar.Nombre;

            this.editRegistroForm.patchValue({
              pkId: isDuplicate ? 0 : +lugar.pkId,
              Nombre: nombreDuplicado,
              Descripcion: lugar.Descripcion,
            });

            this.originalValues = {
              pkId: this.editRegistroForm.value.pkId,
              Nombre: this.editRegistroForm.value.Nombre,
              Descripcion: this.editRegistroForm.value.Descripcion,
            };

            if (isDuplicate) {
              this.mode = "I";
              this.panelService.setDiscardChanges(true);
            } else {
              this.panelService.setDiscardChanges(true);
            }
          }
          this.loading = false;
        })
        .catch((error) => {
          this.httpErrorService.identificarErrores(error);
          this.loading = false;
        });
    } else {
      this.editRegistroForm.patchValue({ pkId: 0, Nombre: "", Descripcion: "" });
      this.loading = false;
      this.panelService.setDiscardChanges(true);
      this.originalValues = {
        pkId: 0,
        Nombre: "",
        Descripcion: "",
      };
    }
  }

  private buildDuplicateName(originalName: string): string {
    const label = this.translation?.Duplicado || "Duplicado";
    const name = originalName || "";

    const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(.*\\(${escapedLabel})(?:\\s+(\\d+))?\\)\\s*$`, "i");
    const match = name.match(regex);

    let newName: string;

    if (!match) {
      newName = `${name} (${label})`;
    } else {
      const prefix = match[1];
      const currentNum = match[2] ? parseInt(match[2], 10) : 0;
      const nextNum = isNaN(currentNum) ? 1 : currentNum + 1;
      newName = `${prefix} ${nextNum})`;
    }

    if (newName.length > this.NOMBRE_MAX_LENGTH) {
      newName = newName.substring(0, this.NOMBRE_MAX_LENGTH);
    }

    return newName;
  }

  discard() {
    if (this.editRegistroForm && this.originalValues) {
      this.editRegistroForm.reset(this.originalValues);
      this.panelService.setDiscardChanges(true);
    }
  }

  submit() {
    this.editRegistroForm.markAllAsTouched();
    if (this.editRegistroForm.invalid) {
      return;
    }

    const value = this.editRegistroForm.value;
    // Determinar Funcion baseado no modo
    // Se modo é "I" (insertar) usar "I", se modo é "U" (modificar) usar "U"
    const pkId = value.pkId || 0;
    // Garantir que ao modificar (modo "U") use FUNCION "U"
    const funcion = this.mode === "I" ? "I" : "U";
    
    const body = {
      pkId: pkId,
      Nombre: value.Nombre,
      Descripcion: value.Descripcion || "",
      Funcion: funcion,
    };

    this.loading = true;
    this.servicioAPI
      .saveLugarAsync(body)
      .then((response) => {
        // Verificar se há mensagens de erro
        if (response?.Mensajes && response.Mensajes.length > 0) {
          response.Mensajes.forEach((m) => {
            const codeKey = `CodErr_${m.CodigoMensaje}`;
            const translated =
              this.translation?.[codeKey] || m.DescripcionMensaje;
            this.snackvar_service.openSnackBar(
              translated,
              "",
              "red-snackbar",
              m.CodigoMensaje
            );
          });
          this.loading = false;
        } else if (response?.DatosResult) {
          // Sucesso - atualizar pkId se foi inserção
          if (funcion === "I" && response.DatosResult.id) {
            this.editRegistroForm.patchValue({
              pkId: response.DatosResult.id,
            });
            this.pkId = String(response.DatosResult.id);
            this.mode = "U"; // Mudar para modo de atualização após inserção
          }
          
          this.snackvar_service.openSnackBar(
            this.translation["MensajeSuccessSnackBar"] ||
              this.translation["GuardadoCorrectamente"] ||
              "Guardado correctamente",
            "",
            "green-snackbar"
          );
          this.panelService.setDiscardChanges(true);
          this.panelService.setSavePanel(null);
          this.closePanel();
        } else {
          // Resposta inesperada
          this.snackvar_service.openSnackBar(
            this.translation["ErrorCargarDatos"] ||
              "Error al guardar los datos",
            "",
            "red-snackbar"
          );
          this.loading = false;
        }
      })
      .catch((error) => {
        this.httpErrorService.identificarErrores(error);
        this.loading = false;
      });
  }

  getErrorMessage(field: string) {
    const control = this.editRegistroForm.get(field);
    if (!control || !control.errors) {
      return "";
    }
    if (control.hasError("required")) {
      return this.translation?.CampoRequerido || "Campo requerido";
    }
    return "Error";
  }

  validateFields(field: string) {
    const control = this.editRegistroForm.get(field);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  closePanel() {
    this.loading = false;
    this.panelService.setClosePanel(null);
  }
}


